/**
 * Feedback Button Component
 *
 * Button that opens a modal for users to submit feedback and suggestions
 * Uses the current dog's theme color for styling
 */

import { useState } from 'react';
import { MessageSquarePlus, Send, Loader2, CheckCircle } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter,
} from '@/shared/components/layout/Modal';
import { useSubmitFeedback } from '@/shared/hooks/useFeedback';
import { useDogThemeStyles } from '@/shared/stores/theme-store';
import { useDogStore } from '@/shared/stores/dog-store';

const MIN_FEEDBACK_LENGTH = 10;
const MAX_FEEDBACK_LENGTH = 2000;

export function FeedbackButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const { mutate: submitFeedback, isPending } = useSubmitFeedback();

  // Get current dog's theme
  const selectedDogId = useDogStore((state) => state.selectedDogId);
  const themeStyles = useDogThemeStyles(selectedDogId);

  const handleOpen = () => {
    setIsOpen(true);
    setText('');
    setShowSuccess(false);
  };

  const handleClose = () => {
    if (!isPending) {
      setIsOpen(false);
      setText('');
      setShowSuccess(false);
    }
  };

  const handleSubmit = () => {
    if (text.trim().length < MIN_FEEDBACK_LENGTH || isPending) return;

    submitFeedback(
      { text: text.trim() },
      {
        onSuccess: () => {
          setShowSuccess(true);
          setTimeout(() => {
            setIsOpen(false);
            setText('');
            setShowSuccess(false);
          }, 2000);
        },
      }
    );
  };

  const isValid = text.trim().length >= MIN_FEEDBACK_LENGTH;
  const charactersLeft = MAX_FEEDBACK_LENGTH - text.length;

  // Dynamic button styles based on theme
  const buttonStyle = themeStyles.isCustom
    ? {
        background: `linear-gradient(135deg, ${themeStyles.preview} 0%, ${themeStyles.preview}dd 100%)`,
        boxShadow: `0 4px 14px 0 ${themeStyles.preview}40`,
      }
    : undefined;

  const buttonClassName = themeStyles.isCustom
    ? ''
    : `bg-gradient-to-r ${themeStyles.gradientClass.replace('bg-gradient-to-r ', '')}`;

  return (
    <>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleOpen}
        className={cn(
          'w-full flex items-center justify-center gap-2',
          'py-3 px-4 rounded-xl',
          'text-white font-medium text-sm',
          'active:scale-[0.98] transition-all duration-200',
          'shadow-sm hover:shadow-md hover:brightness-110',
          buttonClassName
        )}
        style={buttonStyle}
      >
        <MessageSquarePlus className="w-5 h-5" />
        <span>Feedbacks e Sugestões</span>
      </button>

      {/* Modal */}
      <Modal open={isOpen} onOpenChange={setIsOpen}>
        <ModalContent className="max-w-md mx-4">
          {showSuccess ? (
            /* Success State */
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${themeStyles.preview}20` }}
              >
                <CheckCircle
                  className="w-8 h-8"
                  style={{ color: themeStyles.preview }}
                />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Uau, muito obrigado!
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Seu feedback vai nos ajudar a deixar o AIO.dog ainda melhor para voce e seu peludo!
                </p>
              </div>
            </div>
          ) : (
            /* Form State */
            <>
              <ModalHeader>
                <ModalTitle className="flex items-center gap-2">
                  <MessageSquarePlus
                    className="w-5 h-5"
                    style={{ color: themeStyles.preview }}
                  />
                  Queremos te ouvir!
                </ModalTitle>
                <ModalDescription>
                  Encontrou um bug? Tem uma ideia incrível? Quer elogiar ou criticar algo? Este é o lugar certo! Sua opinião nos ajuda a construir o melhor app para você e seu dog.
                </ModalDescription>
              </ModalHeader>

              <div className="py-4">
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value.slice(0, MAX_FEEDBACK_LENGTH))}
                  placeholder="Conta pra gente o que ta pensando... Pode mandar ver, estamos de ouvidos abertos!"
                  className={cn(
                    'w-full h-32 p-3 rounded-lg border resize-none',
                    'text-sm placeholder:text-gray-400',
                    'focus:outline-none focus:ring-2 focus:border-transparent',
                    'transition-all duration-200',
                    text.length > 0 && !isValid
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-gray-200'
                  )}
                  style={{
                    '--tw-ring-color': themeStyles.preview,
                  } as React.CSSProperties}
                  disabled={isPending}
                />

                <div className="flex justify-between items-center mt-2 text-xs">
                  <span
                    className={cn(
                      'transition-colors',
                      text.length > 0 && !isValid
                        ? 'text-amber-600'
                        : 'text-gray-400'
                    )}
                  >
                    {text.length > 0 && !isValid
                      ? `Minimo ${MIN_FEEDBACK_LENGTH} caracteres`
                      : ' '}
                  </span>
                  <span
                    className={cn(
                      'transition-colors',
                      charactersLeft < 50 ? 'text-amber-600' : 'text-gray-400'
                    )}
                  >
                    {charactersLeft} restantes
                  </span>
                </div>
              </div>

              <ModalFooter className="gap-2">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isPending}
                  className={cn(
                    'flex-1 py-2.5 px-4 rounded-lg',
                    'text-sm font-medium text-gray-700',
                    'bg-gray-100 hover:bg-gray-200',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    'transition-colors'
                  )}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!isValid || isPending}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2',
                    'py-2.5 px-4 rounded-lg',
                    'text-sm font-medium text-white',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    'transition-colors hover:brightness-110'
                  )}
                  style={{ backgroundColor: themeStyles.preview }}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Enviar</span>
                    </>
                  )}
                </button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}

FeedbackButton.displayName = 'FeedbackButton';
